import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** A pending wait for one Maps module to fail, see `whenModuleFails`. */
export interface MapsModuleFailureWatch {
  /** Resolves when the module fails to load. Never rejects. */
  promise: Promise<void>;
  /** Stops watching. Call it once the guarded Maps call has settled. */
  cancel(): void;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  // Maps modules (`map`, `marker`, `geocoder`, …) whose lazy fetch failed in
  // the SDK instance on the page (story #11887). The SDK never requests a
  // failed module again, so while this is not empty the instance is broken
  // and the next load replaces it.
  private readonly failedModules = new Set<string>();
  private readonly moduleLoadFailureSubject = new Subject<string>();
  // One SDK load at a time: every load appends a <script> tag.
  private loadInFlight: Promise<void> | null = null;

  /** Emits the name of each Maps module that failed to load. */
  readonly moduleLoadFailure$: Observable<string> =
    this.moduleLoadFailureSubject.asObservable();

  constructor(private zone: NgZone) {}

  loadGoogleMaps(apiKey: string): Promise<void> {
    const loaded = !!(window as any).google?.maps;
    if (!apiKey || (loaded && !this.hasModuleLoadFailed())) {
      return Promise.resolve();
    }
    if (!this.loadInFlight) {
      this.loadInFlight = this.injectScript(apiKey).finally(() => {
        this.loadInFlight = null;
      });
    }
    return this.loadInFlight;
  }

  /**
   * Called by the global error handler when the SDK reports that a module
   * could not be loaded. Map consumers listen on `moduleLoadFailure$`. The
   * error handler runs outside the Angular zone, so the failure is emitted
   * back inside it for the listeners' changes to be rendered.
   */
  reportModuleLoadFailure(module: string): void {
    this.failedModules.add(module);
    this.zone.run(() => this.moduleLoadFailureSubject.next(module));
  }

  hasModuleLoadFailed(): boolean {
    return this.failedModules.size > 0;
  }

  /**
   * Watches for `module` failing to load. A Maps call whose module fails
   * never settles (a geocode never calls back), so callers race it against
   * this. Start watching before making the call: the failure arrives after
   * it, in another task.
   */
  whenModuleFails(module: string): MapsModuleFailureWatch {
    let cancel = () => {};
    const promise = new Promise<void>((resolve) => {
      const subscription = this.moduleLoadFailure$.subscribe((failed) => {
        if (failed === module) {
          subscription.unsubscribe();
          resolve();
        }
      });
      cancel = () => subscription.unsubscribe();
    });
    return { promise, cancel };
  }

  private injectScript(apiKey: string): Promise<void> {
    // A broken SDK instance has to go before the base script is added again,
    // or the new script keeps the old instance and its failed modules. While
    // the fresh script loads, the global is absent, so `google.maps` code that
    // can run meanwhile must check for it. If the fresh script fails, the old
    // instance is put back, so the page is never left without the global.
    const previousGoogle = (window as any).google;
    if (previousGoogle?.maps) {
      delete (window as any).google;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=quarterly&libraries=places&callback=Function.prototype`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.failedModules.clear();
        resolve();
      };
      script.onerror = () => {
        if (previousGoogle?.maps && !(window as any).google) {
          (window as any).google = previousGoogle;
        }
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });
  }
}
